/**
 * EpicGrid component - Tabular display of epics with analysis status and actions
 * Provides epic list table with key, title, and status columns
 * Includes individual epic analysis buttons for unanalyzed epics
 * Implements click handlers for epic navigation
 */

import React, { memo, useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { EpicGridProps, EpicListItem } from '../../../types/epic-types'
import { SkeletonLoader } from '../../Elements/Indicators'
import { EpicGridSkeleton } from '../../Elements/EpicSkeletonLoaders'
import VirtualScrolling from '../../Elements/VirtualScrolling'

// Configuration constants
const VIRTUAL_SCROLL_THRESHOLD = 100 // Enable virtual scrolling for lists > 100 items
const SKELETON_ROWS_COUNT = 5 // Number of skeleton rows to show while loading

// Utility functions
const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString()
  } catch {
    return dateString
  }
}

// Loading skeleton component for table rows
const EpicRowSkeleton: React.FC = memo(() => (
  <tr>
    <td className="px-6 py-4 whitespace-nowrap">
      <SkeletonLoader lines={1} height="h-4" className="w-20" />
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <SkeletonLoader lines={2} height="h-4" className="w-48" />
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <SkeletonLoader lines={1} height="h-4" className="w-24" />
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <SkeletonLoader lines={1} height="h-6" className="w-16" />
    </td>
  </tr>
))

EpicRowSkeleton.displayName = 'EpicRowSkeleton'

// Individual epic row component
interface EpicRowProps {
  epic: EpicListItem
  onEpicClick: (epicKey: string) => void
  onAnalyzeEpic: (epicKey: string) => void
  onEpicHover?: (epicKey: string) => void
  isAnalyzing?: boolean
  isBatchProcessing?: boolean
}

const EpicRow: React.FC<EpicRowProps> = memo(({ epic, onEpicClick, onAnalyzeEpic, onEpicHover, isAnalyzing = false, isBatchProcessing = false }) => {
  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger row click if clicking on the analyze button
    if ((e.target as HTMLElement).closest('button')) {
      return
    }
    // Only allow navigation to analyzed epics
    if (epic.analysisStatus.isAnalyzed) {
      onEpicClick(epic.key)
    }
  }

  const handleAnalyzeClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onAnalyzeEpic(epic.key)
  }

  const handleRowHover = () => {
    if (onEpicHover) {
      onEpicHover(epic.key)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      // Only allow navigation to analyzed epics
      if (epic.analysisStatus.isAnalyzed) {
        onEpicClick(epic.key)
      }
    }
  }

  return (
    <tr
      className={clsx(
        'transition-colors duration-150',
        epic.analysisStatus.isAnalyzed
          ? 'hover:bg-gray-50 cursor-pointer focus-within:bg-blue-50'
          : 'cursor-default opacity-75'
      )}
      onClick={handleRowClick}
      onMouseEnter={handleRowHover}
      onKeyDown={handleKeyDown}
      tabIndex={epic.analysisStatus.isAnalyzed ? 0 : -1}
      role={epic.analysisStatus.isAnalyzed ? "button" : undefined}
      aria-label={epic.analysisStatus.isAnalyzed
        ? `View details for epic ${epic.key}: ${epic.title}`
        : `Epic ${epic.key}: ${epic.title} - Analysis required before viewing details`
      }
      aria-describedby={`epic-status-${epic.key}`}
    >
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <span className={clsx(
          'font-semibold',
          epic.analysisStatus.isAnalyzed
            ? 'text-indigo-600 hover:text-indigo-900'
            : 'text-gray-400'
        )}>
          {epic.key}
        </span>
        {!epic.analysisStatus.isAnalyzed && (
          <span className="ml-2 text-xs text-gray-400" title="Analysis required to view details">
            🔒
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 font-medium">{epic.title}</div>
        <div className="text-sm text-gray-500">
          Completed: {formatDate(epic.completionDate)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          id={`epic-status-${epic.key}`}
          className={clsx(
            'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
            epic.analysisStatus.isAnalyzed
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          )}
          aria-label={`Analysis status: ${epic.analysisStatus.isAnalyzed ? 'Analyzed' : 'Not Analyzed'}`}
        >
          {epic.analysisStatus.isAnalyzed ? 'Analyzed' : 'Not Analyzed'}
        </span>
        {epic.analysisStatus.isAnalyzed && epic.analysisStatus.analysisDate && (
          <div className="text-xs text-gray-500 mt-1" aria-label={`Analyzed on ${formatDate(epic.analysisStatus.analysisDate)}`}>
            {formatDate(epic.analysisStatus.analysisDate)}
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {!epic.analysisStatus.isAnalyzed ? (
          <button
            onClick={handleAnalyzeClick}
            disabled={isAnalyzing || isBatchProcessing}
            className={clsx(
              "inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md transition-colors duration-150",
              (isAnalyzing || isBatchProcessing)
                ? "text-gray-500 bg-gray-100 cursor-not-allowed"
                : "text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            )}
            aria-label={
              isAnalyzing
                ? `Analyzing epic ${epic.key}...`
                : isBatchProcessing
                  ? `Batch analysis in progress - individual analysis disabled`
                  : `Start analysis for epic ${epic.key}`
            }
            type="button"
          >
            {isAnalyzing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Analyzing...
              </>
            ) : isBatchProcessing ? (
              'Batch Processing...'
            ) : (
              'Analyze'
            )}
          </button>
        ) : (
          <span className="text-gray-400" aria-label="No action available - epic already analyzed">—</span>
        )}
      </td>
    </tr>
  )
})

EpicRow.displayName = 'EpicRow'

// Pagination controls component
interface PaginationControlsProps {
  currentPage: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPageChange: (page: number) => void
}

const PaginationControls: React.FC<PaginationControlsProps> = memo(({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange
}) => {
  const startItem = (currentPage - 1) * pageSize + 1
  const endItem = Math.min(currentPage * pageSize, totalItems)

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const getPageNumbers = () => {
    const pages: number[] = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      const start = Math.max(1, currentPage - 2)
      const end = Math.min(totalPages, start + maxVisiblePages - 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
    }

    return pages
  }

  if (totalPages <= 1) {
    return null
  }

  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className={clsx(
            'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md',
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          )}
          aria-label="Go to previous page"
          type="button"
        >
          Previous
        </button>
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className={clsx(
            'ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md',
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          )}
          aria-label="Go to next page"
          type="button"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{startItem}</span> to{' '}
            <span className="font-medium">{endItem}</span> of{' '}
            <span className="font-medium">{totalItems}</span> results
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className={clsx(
                'relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium',
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              )}
              aria-label="Go to previous page"
              type="button"
            >
              <span className="sr-only">Previous</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>

            {getPageNumbers().map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={clsx(
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                  pageNum === currentPage
                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                )}
                aria-label={`Go to page ${pageNum}`}
                aria-current={pageNum === currentPage ? 'page' : undefined}
                type="button"
              >
                {pageNum}
              </button>
            ))}

            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className={clsx(
                'relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium',
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              )}
              aria-label="Go to next page"
              type="button"
            >
              <span className="sr-only">Next</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  )
})

PaginationControls.displayName = 'PaginationControls'

// Main EpicGrid component
export const EpicGrid: React.FC<EpicGridProps> = memo(({
  epics,
  loading = false,
  onEpicClick,
  onAnalyzeEpic,
  onEpicHover,
  pagination,
  onPageChange,
  analyzingEpics = new Set(),
  isBatchProcessing = false
}) => {
  const { page: currentPage, pageSize, totalItems } = pagination
  const totalPages = Math.ceil(totalItems / pageSize)

  // Determine if we should use virtual scrolling
  const useVirtualScrolling = epics.length > VIRTUAL_SCROLL_THRESHOLD

  // Memoize handlers to prevent unnecessary re-renders
  const handleEpicClick = useCallback((epicKey: string) => {
    onEpicClick(epicKey)
  }, [onEpicClick])

  const handleAnalyzeEpic = useCallback((epicKey: string) => {
    onAnalyzeEpic(epicKey)
  }, [onAnalyzeEpic])

  const handleEpicHover = useCallback((epicKey: string) => {
    if (onEpicHover) {
      onEpicHover(epicKey)
    }
  }, [onEpicHover])

  const handlePageChange = useCallback((page: number) => {
    onPageChange(page)
  }, [onPageChange])

  // Memoize empty state to prevent unnecessary re-renders
  const emptyState = useMemo(() => (
    <tr>
      <td colSpan={4} className="px-6 py-12 text-center">
        <div className="text-gray-500">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No epics found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No epics match the current filters. Try adjusting your project or date range.
          </p>
        </div>
      </td>
    </tr>
  ), [])

  // Memoize skeleton rows to prevent recreation on each render
  const skeletonRows = useMemo(() =>
    Array.from({ length: SKELETON_ROWS_COUNT }).map((_, index) => (
      <EpicRowSkeleton key={`skeleton-${index}`} />
    )), []
  )

  // Memoize epic rows to optimize rendering
  const epicRows = useMemo(() =>
    epics.map((epic) => (
      <EpicRow
        key={epic.key}
        epic={epic}
        onEpicClick={handleEpicClick}
        onAnalyzeEpic={handleAnalyzeEpic}
        onEpicHover={handleEpicHover}
        isAnalyzing={analyzingEpics.has(epic.key)}
        isBatchProcessing={isBatchProcessing}
      />
    )), [epics, handleEpicClick, handleAnalyzeEpic, handleEpicHover, analyzingEpics, isBatchProcessing]
  )

  // Virtual scrolling render function
  const renderVirtualItem = useCallback((epic: EpicListItem, index: number) => (
    <EpicRow
      epic={epic}
      onEpicClick={handleEpicClick}
      onAnalyzeEpic={handleAnalyzeEpic}
      onEpicHover={handleEpicHover}
      isAnalyzing={analyzingEpics.has(epic.key)}
      isBatchProcessing={isBatchProcessing}
    />
  ), [handleEpicClick, handleAnalyzeEpic, handleEpicHover, analyzingEpics, isBatchProcessing])

  if (useVirtualScrolling && !loading && epics.length > 0) {
    return (
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Epic Key
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Title & Completion
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Analysis Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
              </table>
              <VirtualScrolling
                items={epics}
                itemHeight={80} // Approximate height of each epic row
                containerHeight={600} // Fixed height for virtual scrolling container
                renderItem={renderVirtualItem}
                overscan={10}
                className="bg-white divide-y divide-gray-200"
              />
            </div>
          </div>
        </div>

        {/* Pagination controls for virtual scrolling */}
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    )
  }

  // Standard table rendering for smaller lists
  return (
    <div className="flex flex-col">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table
              className="min-w-full divide-y divide-gray-200"
              role="table"
              aria-label="Epic analysis list"
              aria-describedby="epic-table-description"
            >
              <caption id="epic-table-description" className="sr-only">
                Table showing epics with their analysis status and available actions. Use arrow keys to navigate and Enter to select.
              </caption>
              <thead className="bg-gray-50">
                <tr role="row">
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    aria-sort="none"
                  >
                    Epic Key
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    aria-sort="none"
                  >
                    Title & Completion
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    aria-sort="none"
                  >
                    Analysis Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    aria-sort="none"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" role="rowgroup">
                {loading ? skeletonRows : epics.length === 0 ? emptyState : epicRows}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination controls */}
      {!loading && epics.length > 0 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  )
})

EpicGrid.displayName = 'EpicGrid'

export default EpicGrid